import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import Panel from "../components/Panel";
import KpiCard from "../components/KpiCard";
import { fmtINR, fmtDate } from "../lib/format";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell, CartesianGrid } from "recharts";
import ExportMenu from "../components/ExportMenu";

const STATUS_COLOR = { "Current": "#4af6c3", "Overdue": "#ff433d", "Disputed": "#ff433d", "Follow-up": "#fb8b1e", "Partially Paid": "#fb8b1e" };

export default function Receivables() {
    const [data, setData] = useState(null);
    const [tab, setTab] = useState("invoices");
    useEffect(() => {
        api.get("/receivables").then((r) => setData(r.data));
    }, []);
    if (!data) return <div className="font-mono-fin text-sm text-[var(--text-secondary)]">// LOADING RECEIVABLES...</div>;

    const ageingData = Object.entries(data.ageing).map(([k, v]) => ({ bucket: k, value: v }));

    return (
        <div className="space-y-4">
            <div className="flex items-start justify-between gap-3">
                <div>
                    <div className="font-mono-fin text-[10px] uppercase tracking-[0.25em] text-[var(--brand-amber)] mb-1">// RECEIVABLE MANAGEMENT</div>
                    <h1 className="font-display font-black text-3xl">Collect What We Earned</h1>
                </div>
                <ExportMenu kind="ar" testId="export-ar" />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <KpiCard testId="ar-total" label="Total Outstanding" value={data.total} sub={`${data.invoices.length} invoices`} accent />
                <KpiCard testId="ar-90plus" label="90+ Days" value={data.ageing["90+"]} sub="Critical recovery" />
                <KpiCard testId="ar-60-90" label="61-90 Days" value={data.ageing["61-90"]} sub="Legal follow-up" />
                <KpiCard testId="ar-current" label="0-30 Days" value={data.ageing["0-30"]} sub="Healthy" />
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-3 gap-3">
                <Panel testId="ar-ageing" title="Ageing Buckets">
                    <div className="h-52">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={ageingData}>
                                <CartesianGrid stroke="#E2E8F0" vertical={false} strokeOpacity={0.5} />
                                <XAxis dataKey="bucket" tick={{ fill: "#8892B0", fontSize: 10 }} />
                                <YAxis tick={{ fill: "#8892B0", fontSize: 10 }} tickFormatter={(v) => `${(v/100000).toFixed(0)}L`} />
                                <Tooltip formatter={(v) => fmtINR(v)} />
                                <Bar dataKey="value">
                                    {ageingData.map((d, i) => <Cell key={i} fill={d.bucket === "0-30" ? "#4af6c3" : d.bucket === "31-60" ? "#fb8b1e" : "#ff433d"} />)}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </Panel>

                <Panel testId="ar-clients" className="xl:col-span-2" title="Client-wise Outstanding">
                    <div className="overflow-x-auto">
                        <table className="w-full font-mono-fin text-xs">
                            <thead className="border-b border-panel">
                                <tr className="text-[10px] uppercase tracking-wider text-[var(--text-secondary)]">
                                    <th className="text-left py-2">Client</th>
                                    <th className="text-left">Type</th>
                                    <th className="text-right">Total</th>
                                    <th className="text-right">Overdue</th>
                                    <th className="text-right">Invoices</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.by_client.sort((a, b) => b.total - a.total).map((c) => (
                                    <tr key={c.client_id} className="border-b border-[var(--border)]/50 hover:bg-[var(--bg-panel-hover)]">
                                        <td className="py-2 text-[var(--text-primary)]">{c.name}</td>
                                        <td><span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 border border-panel">{c.type}</span></td>
                                        <td className="text-right text-[var(--text-primary)]">{fmtINR(c.total)}</td>
                                        <td className={`text-right ${c.overdue > 0 ? "text-neg" : "text-[var(--text-secondary)]"}`}>{c.overdue > 0 ? fmtINR(c.overdue) : "—"}</td>
                                        <td className="text-right text-[var(--text-secondary)]">{c.count}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Panel>
            </div>

            <Panel testId="ar-invoices" title="Invoice Register" subtitle={`${data.invoices.length} open bills`}>
                <div className="overflow-x-auto">
                    <table className="w-full font-mono-fin text-xs">
                        <thead className="border-b border-panel">
                            <tr className="text-[10px] uppercase tracking-wider text-[var(--text-secondary)]">
                                <th className="text-left py-2">Invoice</th>
                                <th className="text-left">Client</th>
                                <th className="text-left">Project</th>
                                <th className="text-left">Type</th>
                                <th className="text-right">Amount</th>
                                <th className="text-right">Outstanding</th>
                                <th className="text-right">Due Date</th>
                                <th className="text-right">Overdue</th>
                                <th className="text-left">Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.invoices.map((r) => (
                                <tr key={r.id} className="border-b border-[var(--border)]/50 hover:bg-[var(--bg-panel-hover)]">
                                    <td className="py-2 text-[var(--text-primary)]">{r.invoice_no}</td>
                                    <td className="text-[var(--text-primary)]">{r.client_name}</td>
                                    <td className="text-[var(--text-secondary)]">{r.project_code}</td>
                                    <td className="text-[var(--text-secondary)] uppercase text-[10px] tracking-wider">{r.type}</td>
                                    <td className="text-right text-[var(--text-primary)]">{fmtINR(r.amount)}</td>
                                    <td className="text-right text-amber">{fmtINR(r.outstanding)}</td>
                                    <td className="text-right text-[var(--text-secondary)]">{fmtDate(r.due_date)}</td>
                                    <td className={`text-right ${r.days_overdue > 0 ? "text-neg" : "text-pos"}`}>{r.days_overdue > 0 ? `${r.days_overdue}d` : "—"}</td>
                                    <td>
                                        <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 border" style={{ borderColor: STATUS_COLOR[r.status] || "#E2E8F0", color: STATUS_COLOR[r.status] || "#8892B0" }}>{r.status}</span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Panel>
        </div>
    );
}
